import { useCallback, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { CanvasBoard } from './CanvasBoard'
import { Grid } from './Grid'
import { SelectionRect } from './SelectionRect'
import { useZoom } from '@/hooks/useZoom'
import { usePan } from '@/hooks/usePan'
import { useSelectionRect } from '@/hooks/useSelectionRect'
import { useSelectionStore, useViewportStore } from '@/stores'

interface CanvasProps {
  children?: ReactNode
  onCanvasContextMenu?: (payload: {
    clientX: number
    clientY: number
    canvasX: number
    canvasY: number
  }) => void
}

/**
 * Main canvas container. Equivalent to #cwrap in the legacy HTML.
 *
 * Responsibilities:
 * - Holds Grid (background pattern) and CanvasBoard (transformable content)
 * - Registers wheel zoom via useZoom
 * - Registers pointer-based pan via usePan
 * - Tracks container dimensions in viewportStore (for fitAll)
 * - Clears selection on click on empty canvas area
 * - Applies user-select: none to prevent accidental text selection while dragging
 */
export function Canvas({ children, onCanvasContextMenu }: CanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const canvasMode = useSelectionStore((s) => s.canvasMode)
  const relMode = useSelectionStore((s) => s.relMode)
  const { isPanning } = usePan(containerRef)
  const { selectionRect, handleCanvasClickCapture } = useSelectionRect(containerRef)
  useZoom(containerRef)

  // Track container dimensions so fitAll can work without DOM access in the store
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const { setContainerSize } = useViewportStore.getState()
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setContainerSize(width, height)
    })
    observer.observe(el)

    // Set initial size immediately
    setContainerSize(el.clientWidth, el.clientHeight)

    return () => observer.disconnect()
  }, [])

  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element
    // Ignore clicks on tables, actions, legends — only clear on bare canvas
    if (target.closest('.etb'))        return
    if (target.closest('[data-action]')) return
    if (target.closest('#legend'))     return
    if (target.closest('#rel-legend')) return
    if (target.closest('[data-relation-editor-affordance="true"]')) return
    if (relMode) {
      useSelectionStore.getState().cancelRelationshipCreation()
    }
    useSelectionStore.getState().clearSelection()
  }, [relMode])

  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!onCanvasContextMenu) return

    const target = event.target as Element
    if (target.closest('.etb')) return
    if (target.closest('[data-action]')) return
    if (target.closest('#legend')) return
    if (target.closest('#rel-legend')) return
    if (target.classList.contains('rel-handle')) return
    if (target.classList.contains('wp-handle')) return
    if (target.tagName.toLowerCase() === 'path') return
    if (target.tagName.toLowerCase() === 'circle') return

    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return

    const point = useViewportStore.getState().clientToCanvas(event.clientX, event.clientY, {
      left: rect.left,
      top: rect.top,
    })

    event.preventDefault()
    event.stopPropagation()
    onCanvasContextMenu({
      clientX: event.clientX,
      clientY: event.clientY,
      canvasX: point.x,
      canvasY: point.y,
    })
  }, [onCanvasContextMenu])

  const cursor = isPanning
    ? 'grabbing'
    : canvasMode === 'pan'
    ? 'grab'
    : 'default'

  return (
    <div
      ref={containerRef}
      id="cwrap"
      data-guide="canvas"
      className="relative min-h-0 flex-1 overflow-hidden"
      style={{
        background: 'var(--canvas-bg)',
        cursor,
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
      onClickCapture={handleCanvasClickCapture}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onAuxClick={(event) => {
        if (event.button === 1) event.preventDefault()
      }}
    >
      <Grid />
      {selectionRect ? <SelectionRect {...selectionRect} /> : null}
      <CanvasBoard>
        {children}
      </CanvasBoard>
    </div>
  )
}
